import type { Commands } from "@/api/commands";
/** Parse and format pinned script://{id}?rev=N references. */

export type ScriptRef = {
  id: string;
  rev: number;
};

export function isScriptUri(value: unknown): boolean {
  return typeof value === "string" && value.startsWith("script://");
}

/** Parse script://{id}?rev=N. Returns null when not a script URI. */
export function parseScriptRef(uri: string): ScriptRef | null {
  if (!uri.startsWith("script://")) return null;
  const rest = uri.slice("script://".length);
  const [idPart = "", query = ""] = rest.split("?");
  const id = idPart.trim();
  if (!id) return null;
  let rev = 0;
  for (const pair of query.split("&")) {
    const [k, v] = pair.split("=");
    if (k === "rev" && v) {
      const n = parseInt(v, 10);
      if (Number.isFinite(n) && n >= 1) rev = n;
    }
  }
  return { id, rev };
}

export function formatScriptRef(id: string, rev: number): string {
  return `script://${id}?rev=${rev}`;
}

/**
 * Resolve strategy source for local Pine parsing.
 * script:// URIs are fetched via script.revision.get (or latest script.source when rev is missing).
 * Inline source is returned as-is.
 */
export async function resolveStrategySource(
  request: Pick<Commands, "getScriptRevision">,
  source: string,
  scripts?: { id: string; source?: string; rev_no?: number }[]
): Promise<string> {
  const ref = parseScriptRef(source);
  if (!ref) return source;

  if (ref.rev >= 1) {
    try {
      const rev = (await request.getScriptRevision({
        id: ref.id,
        rev_no: ref.rev,
      })) as { source?: string };
      if (typeof rev?.source === "string") return rev.source;
    } catch {
      /* fall through */
    }
  }

  const matched = scripts?.find((s) => s.id === ref.id);
  return matched?.source || "";
}
